import { useEffect, useMemo, useState } from 'react';
import { Download, LoaderCircle, Search, ShieldCheck, Sparkles, Star } from 'lucide-react';
import { engineFetchJson } from '@/lib/engineHttp';

interface RegistryListingCard {
  skill_id: string;
  name: string;
  slug: string;
  description: string | null;
  price_credits: number;
  rating: number | null;
  total_ratings: number;
  installs: number;
  published_at: string | null;
  certification_status: string | null;
  listing_tier: string | null;
  verification_depth: string | null;
  bazaar_eligible: boolean | null;
  digital_mark_verified: boolean;
  provenance_verified: boolean;
  license_verified: boolean;
  governance_verified: boolean;
  test_evidence_verified: boolean;
  creator_tenant_id: string;
  creator_name: string;
  creator_avatar_url: string | null;
  creator_metier: string | null;
  creator_verified: boolean;
  has_installed: boolean;
}

interface RegistryResults {
  items: RegistryListingCard[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface RegistryListingDetail {
  skill_id: string;
  slug: string;
  name: string;
  description: string | null;
  short_description: string | null;
  version: string;
  category: string;
  metier: string | null;
  tags: string[];
  required_tools: string[];
  service_contract: Record<string, unknown> | null;
  price_credits: number;
  rating: number | null;
  total_ratings: number;
  installs: number;
  published_at: string | null;
  certification_status: string | null;
  listing_tier: string | null;
  verification_depth: string | null;
  risk_tier: string | null;
  digital_mark_verified: boolean;
  provenance_verified: boolean;
  license_verified: boolean;
  governance_verified: boolean;
  test_evidence_verified: boolean;
  creator_tenant_id: string;
  creator_name: string;
  creator_avatar_url: string | null;
  creator_metier: string | null;
  creator_verified: boolean;
  download_url: string;
  version_history: Array<{
    version: string;
    published_at: string | null;
    certification_status: string | null;
    changelog: string | null;
  }>;
}

interface InstallResponse {
  success: boolean;
  slug: string;
  install_path: string | null;
  message: string;
}

type SortMode = 'relevance' | 'trending' | 'newest' | 'popularity' | 'rating';

const SORT_OPTIONS: Array<{ value: SortMode; label: string }> = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'trending', label: 'Trending' },
  { value: 'newest', label: 'Newest' },
  { value: 'popularity', label: 'Most Installed' },
  { value: 'rating', label: 'Highest Rated' },
];

function formatRating(value: number | null): string {
  if (typeof value !== 'number') return 'Unrated';
  return value.toFixed(1);
}

function formatDate(value: string | null): string {
  if (!value) return 'Recently';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function verificationLabel(item: Pick<RegistryListingCard, 'certification_status' | 'digital_mark_verified' | 'governance_verified'>): string {
  if (item.certification_status === 'certified') return 'Certified';
  if (item.governance_verified || item.digital_mark_verified) return 'Verified';
  if (item.certification_status) return item.certification_status.replace(/_/g, ' ');
  return 'Unverified';
}

function verificationColor(item: Pick<RegistryListingCard, 'certification_status' | 'digital_mark_verified' | 'governance_verified'>): string {
  if (item.certification_status === 'certified') return 'var(--sp-accent)';
  if (item.governance_verified || item.digital_mark_verified) return 'var(--sp-warn)';
  return 'var(--sp-text-dim)';
}

export function TalentsPage() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('trending');
  // Phase 1 (TALENTS-MARKETPLACE-1, 2026-04-19): default OFF.
  // The registry is seeded with provisional listings that have not yet passed
  // ClosedClaw certification. "Verified only" would hide them entirely, making
  // the page look empty. Phase 2 design pass will decide the right default
  // long-term. Walkthrough finding #18 noted the toggle appeared read-only —
  // that was the zero-listings symptom, not a code bug. Now that the registry
  // has listings with mixed certification_status, toggling visibly changes the
  // result set.
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [selectedMetier, setSelectedMetier] = useState<string>('all');
  const [results, setResults] = useState<RegistryResults | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<RegistryListingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [installingSlug, setInstallingSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          page: '1',
          page_size: '24',
          sort_by: sortMode,
          verified_only: String(verifiedOnly),
        });
        if (debouncedQuery) params.set('q', debouncedQuery);
        if (selectedMetier !== 'all') params.set('metier', selectedMetier);

        const data = await engineFetchJson<RegistryResults>(`/registry/browse?${params.toString()}`);
        if (cancelled) return;
        setResults(data);
        if (data.items.length > 0 && !selectedSlug) {
          setSelectedSlug(data.items[0].slug);
        }
        if (data.items.length === 0) {
          setSelectedSlug(null);
          setSelectedDetail(null);
        } else if (selectedSlug && !data.items.some((item) => item.slug === selectedSlug)) {
          setSelectedSlug(data.items[0].slug);
        }
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : 'Failed to load registry');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, selectedMetier, selectedSlug, sortMode, verifiedOnly]);

  useEffect(() => {
    if (!selectedSlug) return;
    let cancelled = false;

    const loadDetail = async () => {
      setDetailLoading(true);
      setDetailError(null);
      try {
        const detail = await engineFetchJson<RegistryListingDetail>(`/registry/detail/${selectedSlug}`);
        if (cancelled) return;
        setSelectedDetail(detail);
      } catch (loadError) {
        if (cancelled) return;
        setDetailError(loadError instanceof Error ? loadError.message : 'Failed to load detail');
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };

    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedSlug]);

  const metierOptions = useMemo(() => {
    const values = new Set<string>();
    results?.items.forEach((item) => {
      if (item.creator_metier?.trim()) values.add(item.creator_metier.trim());
    });
    return ['all', ...Array.from(values).sort((a, b) => a.localeCompare(b))];
  }, [results]);

  const handleInstall = async (slug: string) => {
    setInstallingSlug(slug);
    setNotice(null);
    try {
      const response = await engineFetchJson<InstallResponse>('/talents/install', {
        method: 'POST',
        body: JSON.stringify({ slug }),
      });
      setNotice(response.message || `${slug} installed.`);
      setResults((previous) =>
        previous
          ? {
              ...previous,
              items: previous.items.map((item) =>
                item.slug === slug ? { ...item, has_installed: true } : item,
              ),
            }
          : previous,
      );
    } catch (installError) {
      setNotice(installError instanceof Error ? installError.message : 'Install failed');
    } finally {
      setInstallingSlug(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-space-4)' }}>
      <section
        style={{
          border: '1px solid var(--sp-border)',
          borderRadius: 'var(--sp-radius-lg)',
          background: 'var(--sp-bg-elev)',
          padding: 'var(--sp-space-4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
              Talent Marketplace
            </div>
            <h1 style={{ margin: '6px 0 0', fontSize: 24 }}>Browse developer-grade Talents</h1>
            <p style={{ margin: '8px 0 0', maxWidth: 760, color: 'var(--sp-text-muted)' }}>
              Browse the live Seitiate registry, inspect trust signals, and install Talents into your local runtime. Phase 1 listings are provisional pending ClosedClaw certification; toggle <span className="mono" style={{ fontSize: 11 }}>Verified only</span> once certification lands.
            </p>
          </div>
          <div
            style={{
              minWidth: 180,
              padding: '12px 14px',
              borderRadius: 'var(--sp-radius-lg)',
              background: 'var(--sp-bg-elev-2)',
              border: '1px solid var(--sp-border)',
            }}
          >
            <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
              Live Registry
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
              {results?.total ?? '--'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--sp-text-muted)' }}>listings available</div>
          </div>
        </div>
      </section>

      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 340px',
          gap: 'var(--sp-space-4)',
          minHeight: 560,
        }}
      >
        <div
          style={{
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius-lg)',
            background: 'var(--sp-bg-elev)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: 'var(--sp-space-4)',
              borderBottom: '1px solid var(--sp-border)',
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 170px 160px',
              gap: 'var(--sp-space-2)',
              alignItems: 'center',
            }}
          >
            <label style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--sp-text-dim)' }} />
              <input
                className="sp-input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search talents, creators, descriptions..."
                style={{ paddingLeft: 32 }}
              />
            </label>
            <select className="sp-input" value={sortMode} onChange={(event) => setSortMode(event.target.value as SortMode)}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              role="switch"
              aria-checked={verifiedOnly}
              onClick={() => setVerifiedOnly((v) => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 'var(--sp-radius)',
                border: `1px solid ${verifiedOnly ? 'rgba(127, 209, 185, 0.35)' : 'var(--sp-border)'}`,
                background: verifiedOnly ? 'rgba(127, 209, 185, 0.12)' : 'var(--sp-bg-elev-2)',
                color: verifiedOnly ? 'var(--sp-accent)' : 'var(--sp-text-muted)',
                cursor: 'pointer',
                font: 'inherit',
                textAlign: 'left',
              }}
            >
              <span>Verified only</span>
              <span
                aria-hidden="true"
                style={{
                  width: 28,
                  height: 16,
                  borderRadius: 999,
                  background: verifiedOnly ? 'var(--sp-accent)' : 'var(--sp-border-strong)',
                  position: 'relative',
                  transition: 'background 120ms ease',
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: verifiedOnly ? 14 : 2,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: '#0b0d10',
                    transition: 'left 120ms ease',
                  }}
                />
              </span>
            </button>
          </div>

          <div
            style={{
              padding: 'var(--sp-space-3) var(--sp-space-4)',
              borderBottom: '1px solid var(--sp-border)',
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            {metierOptions.map((metier) => {
              const active = selectedMetier === metier;
              return (
                <button
                  key={metier}
                  type="button"
                  className="sp-btn"
                  onClick={() => setSelectedMetier(metier)}
                  style={{
                    background: active ? 'rgba(127, 209, 185, 0.12)' : 'var(--sp-bg-elev-2)',
                    borderColor: active ? 'rgba(127, 209, 185, 0.3)' : 'var(--sp-border)',
                    color: active ? 'var(--sp-accent)' : 'var(--sp-text-muted)',
                  }}
                >
                  {metier === 'all' ? 'All domains' : metier}
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-space-4)' }}>
            {loading ? (
              <div className="sp-empty">
                <LoaderCircle size={18} className="animate-spin" style={{ marginBottom: 8 }} />
                Loading registry...
              </div>
            ) : error ? (
              <div className="sp-empty" style={{ color: 'var(--sp-error)' }}>
                {error}
              </div>
            ) : !results || results.items.length === 0 ? (
              <div className="sp-empty">
                {(() => {
                  // Walkthrough finding #17: empty state must differentiate
                  // (a) filter-mismatch, (b) verified-only-hides-everything,
                  // (c) truly-empty registry.
                  const hasFilter = Boolean(debouncedQuery) || selectedMetier !== 'all';
                  if (results && results.total === 0 && !hasFilter && !verifiedOnly) {
                    return 'The registry has no listings yet. Install Talents by URL or CLI while the marketplace is seeded.';
                  }
                  if (verifiedOnly && !hasFilter) {
                    return 'No certified listings yet. Uncheck "Verified only" to see provisional listings awaiting ClosedClaw certification.';
                  }
                  return 'No listings matched your filters. Clear the search or switch domain to see more.';
                })()}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                {results.items.map((item) => {
                  const active = item.slug === selectedSlug;
                  const verification = verificationLabel(item);
                  const verificationTone = verificationColor(item);
                  return (
                    <button
                      key={item.slug}
                      type="button"
                      onClick={() => setSelectedSlug(item.slug)}
                      style={{
                        textAlign: 'left',
                        borderRadius: 'var(--sp-radius-lg)',
                        border: active ? '1px solid rgba(127, 209, 185, 0.35)' : '1px solid var(--sp-border)',
                        background: active ? 'rgba(127, 209, 185, 0.06)' : 'var(--sp-bg-elev-2)',
                        color: 'var(--sp-text)',
                        padding: '14px',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700 }}>{item.name}</div>
                          <div style={{ marginTop: 4, color: 'var(--sp-text-muted)', fontSize: 12 }}>
                            by {item.creator_name}
                          </div>
                        </div>
                        <span
                          className="mono"
                          style={{
                            fontSize: 10,
                            color: verificationTone,
                            border: `1px solid ${verificationTone}`,
                            borderRadius: 999,
                            padding: '2px 8px',
                            textTransform: 'uppercase',
                          }}
                        >
                          {verification}
                        </span>
                      </div>

                      <div
                        style={{
                          marginTop: 10,
                          color: 'var(--sp-text-muted)',
                          fontSize: 13,
                          minHeight: 58,
                          lineHeight: 1.55,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {item.description || 'No description yet.'}
                      </div>

                      <div style={{ display: 'flex', gap: 14, marginTop: 12, fontSize: 12, color: 'var(--sp-text-muted)' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Star size={12} />
                          {formatRating(item.rating)}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Download size={12} />
                          {item.installs}
                        </span>
                        <span>{formatDate(item.published_at)}</span>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 14, alignItems: 'center' }}>
                        <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
                          {item.creator_metier || item.listing_tier || 'Registry listing'}
                        </div>
                        {item.has_installed ? (
                          <span
                            className="mono"
                            style={{
                              fontSize: 10,
                              color: 'var(--sp-accent)',
                              border: '1px solid rgba(127, 209, 185, 0.25)',
                              borderRadius: 999,
                              padding: '4px 8px',
                            }}
                          >
                            Installed
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="sp-btn sp-btn-primary"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleInstall(item.slug);
                            }}
                            disabled={installingSlug === item.slug}
                          >
                            {installingSlug === item.slug ? 'Installing...' : 'Install'}
                          </button>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <aside
          style={{
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius-lg)',
            background: 'var(--sp-bg-elev)',
            padding: 'var(--sp-space-4)',
            overflowY: 'auto',
          }}
        >
          {notice ? (
            <div
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 'var(--sp-radius)',
                border: '1px solid rgba(127, 209, 185, 0.25)',
                background: 'rgba(127, 209, 185, 0.08)',
                color: 'var(--sp-accent)',
                fontSize: 12,
              }}
            >
              {notice}
            </div>
          ) : null}

          {!selectedSlug ? (
            <div className="sp-empty">Select a listing to inspect its detail.</div>
          ) : detailLoading ? (
            <div className="sp-empty">
              <LoaderCircle size={18} className="animate-spin" style={{ marginBottom: 8 }} />
              Loading detail...
            </div>
          ) : detailError ? (
            <div className="sp-empty" style={{ color: 'var(--sp-error)' }}>
              {detailError}
            </div>
          ) : selectedDetail ? (
            <div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                Detail
              </div>
              <h2 style={{ margin: '8px 0 0', fontSize: 22 }}>{selectedDetail.name}</h2>
              <div style={{ marginTop: 6, color: 'var(--sp-text-muted)', fontSize: 13 }}>
                {selectedDetail.creator_name} · {selectedDetail.category}
                {selectedDetail.metier ? ` · ${selectedDetail.metier}` : ''}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 16 }}>
                <Metric label="Version" value={selectedDetail.version} />
                <Metric label="Risk tier" value={selectedDetail.risk_tier || 'Not declared'} />
                <Metric label="Rating" value={formatRating(selectedDetail.rating)} />
                <Metric label="Installs" value={String(selectedDetail.installs)} />
              </div>

              <div style={{ marginTop: 18 }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                  Description
                </div>
                <p style={{ marginTop: 8, color: 'var(--sp-text-muted)', lineHeight: 1.7 }}>
                  {selectedDetail.description || selectedDetail.short_description || 'No detail provided.'}
                </p>
              </div>

              <div style={{ marginTop: 18 }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                  Trust Signals
                </div>
                <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                  <SignalRow label="Certification" active={selectedDetail.certification_status === 'certified'} />
                  <SignalRow label="Digital mark verified" active={selectedDetail.digital_mark_verified} />
                  <SignalRow label="Provenance verified" active={selectedDetail.provenance_verified} />
                  <SignalRow label="Governance verified" active={selectedDetail.governance_verified} />
                  <SignalRow label="Test evidence verified" active={selectedDetail.test_evidence_verified} />
                </div>
              </div>

              {selectedDetail.tags.length > 0 ? (
                <div style={{ marginTop: 18 }}>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                    Tags
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    {selectedDetail.tags.map((tag) => (
                      <span
                        key={tag}
                        className="mono"
                        style={{
                          fontSize: 10,
                          color: 'var(--sp-text-muted)',
                          border: '1px solid var(--sp-border)',
                          borderRadius: 999,
                          padding: '4px 8px',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {selectedDetail.required_tools.length > 0 ? (
                <div style={{ marginTop: 18 }}>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                    Required Tools
                  </div>
                  <ul style={{ margin: '10px 0 0', paddingLeft: 18, color: 'var(--sp-text-muted)' }}>
                    {selectedDetail.required_tools.map((tool) => (
                      <li key={tool}>{tool}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="sp-btn sp-btn-primary"
                  onClick={() => void handleInstall(selectedDetail.slug)}
                  disabled={installingSlug === selectedDetail.slug}
                >
                  {installingSlug === selectedDetail.slug ? 'Installing...' : 'Install Talent'}
                </button>
                <a
                  className="sp-btn"
                  href={selectedDetail.download_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  Download Archive
                </a>
              </div>
            </div>
          ) : (
            <div className="sp-empty">Select a listing to inspect its detail.</div>
          )}
        </aside>
      </section>

      <WantedPanel />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        border: '1px solid var(--sp-border)',
        borderRadius: 'var(--sp-radius)',
        background: 'var(--sp-bg-elev-2)',
        padding: '10px 12px',
      }}
    >
      <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

/**
 * Wanted in the registry — design reference panel.
 *
 * Phase 1 (TALENTS-MARKETPLACE-1, 2026-04-19): the Director-ratified
 * alternative to "Coming soon" placeholder cards. These slots are
 * commission opportunities, NOT unshipped products. They give Phase 2
 * designers a visual reference for domain + trust-tier variety without
 * pretending the marketplace has more than it does.
 *
 * No install buttons. No fake ratings. No "Coming soon" badge.
 * The framing is "open commissions" — the ecosystem has shape and gaps.
 *
 * List authored by the dispatch prompt + Director ruling. Phase 2 will
 * refine this into TALENTS-COMMISSION-BACKLOG-V1.md.
 */
interface WantedEntry {
  slug: string;
  domain: string;
  tier: 'PROMPT_ONLY' | 'TOOL_USING' | 'EXECUTABLE';
  wanted_for: string;
}

const WANTED_IN_REGISTRY: WantedEntry[] = [
  { slug: 'repo_indexer', domain: 'Code', tier: 'EXECUTABLE', wanted_for: 'Desktop launch — lets agents read a developer\'s repo as Codex context.' },
  { slug: 'pr_reviewer', domain: 'Code', tier: 'EXECUTABLE', wanted_for: 'Developer daily loop — flagship use case for the desktop hacker audience.' },
  { slug: 'canon_evaluator', domain: 'Governance', tier: 'TOOL_USING', wanted_for: 'Thesis legibility — shows what Canon-aware quality enforcement looks like.' },
  { slug: 'writing_sprint', domain: 'Writing', tier: 'PROMPT_ONLY', wanted_for: 'Third-party authorship example — proves non-core contributors can ship.' },
  { slug: 'memoir_from_garden', domain: 'Composed', tier: 'TOOL_USING', wanted_for: 'Upstream/downstream chain demo — composes garden_harvester + scholar_finder.' },
  { slug: 'test_generator', domain: 'Code', tier: 'EXECUTABLE', wanted_for: 'Developer workflow — test scaffolding from function signatures.' },
];

function tierColor(tier: WantedEntry['tier']): string {
  switch (tier) {
    case 'PROMPT_ONLY':
      return 'var(--sp-accent)';
    case 'TOOL_USING':
      return 'var(--sp-warn)';
    case 'EXECUTABLE':
      return 'var(--sp-error)';
  }
}

function WantedPanel() {
  return (
    <section
      style={{
        border: '1px dashed var(--sp-border)',
        borderRadius: 'var(--sp-radius-lg)',
        background: 'var(--sp-bg-elev)',
        padding: 'var(--sp-space-4)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
            Wanted in the Registry
          </div>
          <h2 style={{ margin: '6px 0 0', fontSize: 18 }}>Open commissions</h2>
          <p style={{ margin: '8px 0 0', maxWidth: 760, color: 'var(--sp-text-muted)', fontSize: 13, lineHeight: 1.6 }}>
            Talents the ecosystem wants next. These are not products — they are open commissions. Nothing below ships, nothing installs. Phase 2 of the marketplace design pass will refine this into a real commission backlog.
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginTop: 16 }}>
        {WANTED_IN_REGISTRY.map((entry) => (
          <div
            key={entry.slug}
            style={{
              border: '1px dashed var(--sp-border)',
              borderRadius: 'var(--sp-radius)',
              background: 'var(--sp-bg-elev-2)',
              padding: 14,
              opacity: 0.85,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
              <div>
                <div className="mono" style={{ fontSize: 13, fontWeight: 600, color: 'var(--sp-text)' }}>
                  {entry.slug}
                </div>
                <div style={{ marginTop: 4, color: 'var(--sp-text-muted)', fontSize: 12 }}>
                  {entry.domain}
                </div>
              </div>
              <span
                className="mono"
                style={{
                  fontSize: 9,
                  color: tierColor(entry.tier),
                  border: `1px solid ${tierColor(entry.tier)}`,
                  borderRadius: 999,
                  padding: '2px 8px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                {entry.tier}
              </span>
            </div>
            <div style={{ marginTop: 12, color: 'var(--sp-text-muted)', fontSize: 12, lineHeight: 1.55 }}>
              <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase', marginRight: 6 }}>
                Wanted for
              </span>
              {entry.wanted_for}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SignalRow({ label, active }: { label: string; active: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: '1px solid var(--sp-border)',
        borderRadius: 'var(--sp-radius)',
        background: 'var(--sp-bg-elev-2)',
        padding: '9px 12px',
        color: 'var(--sp-text-muted)',
      }}
    >
      <span>{label}</span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: active ? 'var(--sp-accent)' : 'var(--sp-text-dim)',
          fontSize: 12,
        }}
      >
        {active ? <ShieldCheck size={14} /> : <Sparkles size={14} />}
        {active ? 'Yes' : 'No'}
      </span>
    </div>
  );
}
